import * as THREE from 'three';

import { EngineStar } from '../types';

import { StarsSystem } from './StarsSystem';

interface SupernovaParticleData {
  baseRadius: number;
  spinSpeed: number;
  spinAxis: THREE.Vector3;
  phase: number;
  explosionDir: THREE.Vector3;
  explosionSpeed: number;
}

interface BurstParticleData {
  velocity: THREE.Vector3;
  delay: number;
}

interface CollapseData {
  radius: number;
  clockwise: number;
  affectedStars: number[];
  originalPositions: Array<{ x: number; y: number; z: number }>;
  centerPosition: THREE.Vector3;
}

interface ActiveEffect {
  group: THREE.Group;
  life: number;
  maxLife: number;
  traderIndex: number;
  type: 'supernova' | 'collapse';
  coreData?: SupernovaParticleData[];
  burstData?: BurstParticleData[];
  collapseData?: CollapseData;
}

/**
 * Trade effects: supernova (profit) and collapse (loss).
 * Ported from: hooks/useTradeEffects.ts
 */
export class EffectsSystem {
  private effects: ActiveEffect[] = [];
  private elapsed = 0;

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    private starsSystem: StarsSystem
  ) {}

  triggerEffect(traderIndex: number, type: 'supernova' | 'collapse', intensity: number): void {
    const points = this.starsSystem.getPoints();
    if (!points) return;

    const positions = points.geometry.attributes.position.array;
    const position = new THREE.Vector3(
      positions[traderIndex * 3],
      positions[traderIndex * 3 + 1],
      positions[traderIndex * 3 + 2]
    );

    if (type === 'supernova') {
      const { group, coreData, burstData } = createSupernovaEffect(position, intensity);
      this.scene.add(group);
      this.effects.push({
        group,
        life: 0,
        maxLife: 180,
        traderIndex,
        type: 'supernova',
        coreData,
        burstData,
      });
    } else {
      const { group, collapseData } = createCollapseEffect(position, intensity, points);
      this.scene.add(group);
      this.effects.push({
        group,
        life: 0,
        maxLife: 250,
        traderIndex,
        type: 'collapse',
        collapseData,
      });
    }
  }

  animate(dt: number, stars: EngineStar[]): void {
    const points = this.starsSystem.getPoints();
    if (!points) return;

    this.elapsed += dt;
    const elapsed = this.elapsed;

    for (let i = this.effects.length - 1; i >= 0; i--) {
      const effect = this.effects[i];
      effect.life++;
      const progress = effect.life / effect.maxLife;

      if (progress >= 1) {
        this.scene.remove(effect.group);
        disposeGroup(effect.group);
        this.effects.splice(i, 1);
        continue;
      }

      const positions = points.geometry.attributes.position.array;
      if (effect.type === 'supernova') {
        effect.group.position.set(
          positions[effect.traderIndex * 3],
          positions[effect.traderIndex * 3 + 1],
          positions[effect.traderIndex * 3 + 2]
        );
        animateSupernova(effect, progress, elapsed, effect.coreData!, effect.burstData!);
      } else if (effect.collapseData) {
        animateCollapse(effect, progress, elapsed, effect.collapseData, points, stars, this.camera);
      }
    }
  }

  destroy(): void {
    for (const effect of this.effects) {
      this.scene.remove(effect.group);
      disposeGroup(effect.group);
    }
    this.effects = [];
  }
}

// --- Helpers ---

function disposeGroup(group: THREE.Group): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.Points) {
      child.geometry.dispose();
      if (child.material instanceof THREE.Material) child.material.dispose();
    }
  });
}

// --- SUPERNOVA ---

function createSupernovaEffect(
  position: THREE.Vector3,
  intensity: number
): { group: THREE.Group; coreData: SupernovaParticleData[]; burstData: BurstParticleData[] } {
  const group = new THREE.Group();
  group.position.copy(position);

  const sizeScale = 1 + intensity * 1.5;
  const coreCount = 80 + Math.floor(intensity * 60);
  const coreGeometry = new THREE.BufferGeometry();
  const corePositions = new Float32Array(coreCount * 3);
  const coreSizes = new Float32Array(coreCount);
  const coreData: SupernovaParticleData[] = [];

  for (let i = 0; i < coreCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const r = (0.2 + Math.random() * 0.8) * sizeScale;
    corePositions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    corePositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    corePositions[i * 3 + 2] = r * Math.cos(phi);
    coreSizes[i] = 0.8 + Math.random() * 1.2;

    coreData.push({
      baseRadius: r,
      spinSpeed: (0.3 + Math.random() * 0.5) * (Math.random() > 0.5 ? 1 : -1),
      spinAxis: new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5
      ).normalize(),
      phase: Math.random() * Math.PI * 2,
      explosionDir: new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5
      ).normalize(),
      explosionSpeed: 0.15 + Math.random() * 0.25,
    });
  }

  coreGeometry.setAttribute('position', new THREE.BufferAttribute(corePositions, 3));
  coreGeometry.setAttribute('size', new THREE.BufferAttribute(coreSizes, 1));

  const coreMaterial = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, phase: { value: 0 } },
    vertexShader: `
      attribute float size;
      varying float vBrightness;
      void main() {
        vBrightness = 0.7 + size * 0.3;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * (200.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform float phase;
      varying float vBrightness;
      void main() {
        float dist = length(gl_PointCoord - vec2(0.5));
        if (dist > 0.5) discard;
        float core = 1.0 - smoothstep(0.0, 0.15, dist);
        float glow = exp(-dist * dist * 12.0);
        float alpha = (core + glow * 0.5) * (1.0 - time * 0.8);
        vec3 color = mix(vec3(0.6, 1.0, 0.7), vec3(1.0, 1.0, 0.9), vBrightness * 0.5);
        float sparkle = sin(phase * 20.0 + gl_PointCoord.x * 50.0) * 0.3 + 0.7;
        gl_FragColor = vec4(color * sparkle, alpha * vBrightness);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const coreParticles = new THREE.Points(coreGeometry, coreMaterial);
  coreParticles.userData.type = 'supernovaCore';
  group.add(coreParticles);

  // Burst particles
  const burstCount = 40 + Math.floor(intensity * 30);
  const burstGeometry = new THREE.BufferGeometry();
  const burstPositions = new Float32Array(burstCount * 3);
  const burstSizes = new Float32Array(burstCount);
  const burstData: BurstParticleData[] = [];

  for (let i = 0; i < burstCount; i++) {
    burstPositions[i * 3] = 0;
    burstPositions[i * 3 + 1] = 0;
    burstPositions[i * 3 + 2] = 0;
    burstSizes[i] = 1.0 + Math.random() * 1.5;

    const bTheta = Math.random() * Math.PI * 2;
    const bPhi = Math.acos(2 * Math.random() - 1);
    burstData.push({
      velocity: new THREE.Vector3(
        Math.sin(bPhi) * Math.cos(bTheta),
        Math.sin(bPhi) * Math.sin(bTheta),
        Math.cos(bPhi)
      ).multiplyScalar(0.3 + Math.random() * 0.4),
      delay: Math.random() * 0.3,
    });
  }

  burstGeometry.setAttribute('position', new THREE.BufferAttribute(burstPositions, 3));
  burstGeometry.setAttribute('size', new THREE.BufferAttribute(burstSizes, 1));

  const burstMaterial = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 } },
    vertexShader: `
      attribute float size;
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * (160.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform float time;
      void main() {
        float dist = length(gl_PointCoord - vec2(0.5));
        if (dist > 0.5) discard;
        float alpha = (1.0 - dist * 2.0) * (1.0 - time);
        gl_FragColor = vec4(0.7, 1.0, 0.8, alpha * 0.8);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const burstParticles = new THREE.Points(burstGeometry, burstMaterial);
  burstParticles.userData.type = 'supernovaBurst';
  group.add(burstParticles);

  // Central flash
  const flashMaterial = new THREE.MeshBasicMaterial({
    color: 0xccffcc,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
  });
  const flash = new THREE.Mesh(
    new THREE.SphereGeometry(0.2 * (1 + intensity * 2), 12, 12),
    flashMaterial
  );
  flash.userData.type = 'flash';
  group.add(flash);

  return { group, coreData, burstData };
}

function animateSupernova(
  effect: ActiveEffect,
  progress: number,
  elapsed: number,
  coreData: SupernovaParticleData[],
  burstData: BurstParticleData[]
): void {
  for (const child of effect.group.children) {
    if (child.userData.type === 'supernovaCore' && child instanceof THREE.Points) {
      const pos = child.geometry.attributes.position.array as Float32Array;
      (child.material as THREE.ShaderMaterial).uniforms.time.value = progress;
      (child.material as THREE.ShaderMaterial).uniforms.phase.value = elapsed;

      for (let p = 0; p < coreData.length; p++) {
        const d = coreData[p];
        const spinPhase = Math.min(progress / 0.3, 1.0);
        const explosionPhase = Math.max(0, (progress - 0.3) / 0.7);

        const spinAngle = d.phase + elapsed * d.spinSpeed * (1 + (1 - spinPhase) * 3);
        const spinRadius = d.baseRadius * (0.5 + spinPhase * 0.5);
        const explosionRadius = explosionPhase * d.explosionSpeed * 15;

        let x = spinRadius * Math.cos(spinAngle);
        let y = spinRadius * Math.sin(spinAngle) * d.spinAxis.y;
        let z = spinRadius * Math.sin(spinAngle) * d.spinAxis.z;

        if (spinPhase < 1) {
          x += Math.sin(elapsed * 8 + p) * 0.3 * (1 - spinPhase);
          y += Math.cos(elapsed * 7 + p * 0.5) * 0.3 * (1 - spinPhase);
          z += Math.sin(elapsed * 6 + p * 0.7) * 0.3 * (1 - spinPhase);
        }

        x += d.explosionDir.x * explosionRadius;
        y += d.explosionDir.y * explosionRadius;
        z += d.explosionDir.z * explosionRadius;

        pos[p * 3] = x;
        pos[p * 3 + 1] = y;
        pos[p * 3 + 2] = z;
      }
      child.geometry.attributes.position.needsUpdate = true;
    }

    if (child.userData.type === 'supernovaBurst' && child instanceof THREE.Points) {
      const pos = child.geometry.attributes.position.array as Float32Array;
      (child.material as THREE.ShaderMaterial).uniforms.time.value = progress;

      for (let p = 0; p < burstData.length; p++) {
        const d = burstData[p];
        const adj = Math.max(0, progress - d.delay);
        if (adj > 0) {
          pos[p * 3] = d.velocity.x * adj * 20;
          pos[p * 3 + 1] = d.velocity.y * adj * 20;
          pos[p * 3 + 2] = d.velocity.z * adj * 20;
        }
      }
      child.geometry.attributes.position.needsUpdate = true;
    }

    if (child.userData.type === 'flash' && child instanceof THREE.Mesh) {
      child.scale.setScalar(1 + progress * 3);
      (child.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 1 - progress * 2);
    }
  }
}

// --- COLLAPSE ---

function createCollapseEffect(
  position: THREE.Vector3,
  intensity: number,
  points: THREE.Points
): { group: THREE.Group; collapseData: CollapseData } {
  const group = new THREE.Group();
  group.position.copy(position);

  const sizeScale = 1 + intensity * 2;
  const radius = 2.5 * sizeScale * 0.7;
  const clockwise = Math.random() > 0.5 ? 1 : -1;

  const affectedStars: number[] = [];
  const originalPositions: Array<{ x: number; y: number; z: number }> = [];
  const starPositions = points.geometry.attributes.position.array;
  const count = starPositions.length / 3;

  for (let i = 0; i < count; i++) {
    const dx = starPositions[i * 3] - position.x;
    const dy = starPositions[i * 3 + 1] - position.y;
    const dz = starPositions[i * 3 + 2] - position.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < radius && dist > 0.5) {
      affectedStars.push(i);
      originalPositions.push({
        x: starPositions[i * 3],
        y: starPositions[i * 3 + 1],
        z: starPositions[i * 3 + 2],
      });
    }
  }

  // Fire ring
  const ringCurve = new THREE.EllipseCurve(0, 0, radius, radius, 0, Math.PI * 2, false, 0);
  const ringPoints2d = ringCurve.getPoints(64);
  const ringPath = new THREE.CatmullRomCurve3(
    ringPoints2d.map((p) => new THREE.Vector3(p.x, 0, p.y))
  );
  ringPath.closed = true;

  const fireRingGeometry = new THREE.TubeGeometry(ringPath, 64, radius * 0.04, 8, true);
  const fireRingMaterial = new THREE.ShaderMaterial({
    uniforms: {
      progress: { value: 0 },
      clockwise: { value: clockwise },
      time: { value: 0 },
      opacity: { value: 1.0 },
    },
    vertexShader: `
      varying vec3 vPosition;
      void main() {
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float progress;
      uniform float clockwise;
      uniform float time;
      uniform float opacity;
      varying vec3 vPosition;
      void main() {
        float angle = atan(vPosition.z, vPosition.x);
        if (angle < 0.0) angle += 6.28318;
        float adjustedAngle = clockwise > 0.0 ? angle : (6.28318 - angle);
        float targetAngle = progress * 6.28318;
        if (adjustedAngle > targetAngle + 0.05) discard;
        float distFromTip = targetAngle - adjustedAngle;
        float tipBrightness = exp(-distFromTip * 2.0);
        float flicker = 0.85 + sin(time * 25.0 + angle * 8.0) * 0.15;
        vec3 tipColor = vec3(1.0, 0.95, 0.6);
        vec3 trailColor = vec3(1.0, 0.35, 0.05);
        vec3 color = mix(trailColor, tipColor, tipBrightness);
        float alpha = (0.7 + tipBrightness * 0.3) * flicker * opacity;
        gl_FragColor = vec4(color, alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const fireRing = new THREE.Mesh(fireRingGeometry, fireRingMaterial);
  fireRing.userData.type = 'fireRing';
  group.add(fireRing);

  // Void disc
  const voidMaterial = new THREE.ShaderMaterial({
    uniforms: { opacity: { value: 0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float opacity;
      void main() {
        float dist = length(vUv - vec2(0.5)) * 2.0;
        float alpha = (1.0 - dist) * opacity * 0.3;
        gl_FragColor = vec4(0.1, 0.0, 0.0, alpha);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const voidDisc = new THREE.Mesh(new THREE.CircleGeometry(radius * 0.95, 32), voidMaterial);
  voidDisc.userData.type = 'voidDisc';
  group.add(voidDisc);

  return {
    group,
    collapseData: {
      radius,
      clockwise,
      affectedStars,
      originalPositions,
      centerPosition: position.clone(),
    },
  };
}

function animateCollapse(
  effect: ActiveEffect,
  progress: number,
  elapsed: number,
  collapseData: CollapseData,
  points: THREE.Points,
  stars: EngineStar[],
  camera: THREE.PerspectiveCamera
): void {
  const center = collapseData.centerPosition;
  const positions = points.geometry.attributes.position.array as Float32Array;
  const idx = effect.traderIndex;

  if (idx >= 0) {
    const newPos = new THREE.Vector3(
      positions[idx * 3],
      positions[idx * 3 + 1],
      positions[idx * 3 + 2]
    );
    effect.group.position.copy(newPos);
    center.copy(newPos);
  }

  for (const child of effect.group.children) {
    if (child.userData.type === 'fireRing' && child instanceof THREE.Mesh) {
      const igniteEnd = 0.15;
      const fadeEnd = 0.05;
      const igniteProgress = Math.min(progress / igniteEnd, 1.0);
      (child.material as THREE.ShaderMaterial).uniforms.progress.value = igniteProgress;
      (child.material as THREE.ShaderMaterial).uniforms.time.value = elapsed;
      (child.material as THREE.ShaderMaterial).uniforms.opacity.value =
        progress > igniteEnd ? Math.max(0, 1 - (progress - igniteEnd) / fadeEnd) : 1.0;
    }

    if (child.userData.type === 'voidDisc' && child instanceof THREE.Mesh) {
      if (progress > 0.3 && progress < 0.8) {
        const voidProgress = (progress - 0.3) / 0.5;
        (child.material as THREE.ShaderMaterial).uniforms.opacity.value =
          Math.sin(voidProgress * Math.PI) * 0.8;
      } else {
        (child.material as THREE.ShaderMaterial).uniforms.opacity.value = 0;
      }
      child.lookAt(camera.position);
    }
  }

  // Animate affected stars
  if (collapseData.affectedStars.length > 0) {
    const starPositions = points.geometry.attributes.position.array as Float32Array;
    const starSizes = points.geometry.attributes.size.array as Float32Array;

    for (let s = 0; s < collapseData.affectedStars.length; s++) {
      const starIdx = collapseData.affectedStars[s];
      const orig = collapseData.originalPositions[s];

      if (progress < 0.075) {
        const flicker = 0.8 + Math.sin(elapsed * 20 + s) * 0.2;
        starSizes[starIdx] = stars[starIdx].size * flicker;
      } else if (progress < 0.15) {
        const implodeProgress = (progress - 0.075) / 0.075;
        const eased = implodeProgress * implodeProgress;
        starPositions[starIdx * 3] = orig.x + (center.x - orig.x) * eased;
        starPositions[starIdx * 3 + 1] = orig.y + (center.y - orig.y) * eased;
        starPositions[starIdx * 3 + 2] = orig.z + (center.z - orig.z) * eased;
        starSizes[starIdx] = stars[starIdx].size * (1 - eased * 0.95);
      } else if (progress < 0.5) {
        starPositions[starIdx * 3] = center.x;
        starPositions[starIdx * 3 + 1] = center.y;
        starPositions[starIdx * 3 + 2] = center.z;
        starSizes[starIdx] = 0.05;
      } else {
        const restoreProgress = (progress - 0.5) / 0.5;
        const eased = 1 - Math.pow(1 - restoreProgress, 3);
        starPositions[starIdx * 3] = orig.x;
        starPositions[starIdx * 3 + 1] = orig.y;
        starPositions[starIdx * 3 + 2] = orig.z;
        starSizes[starIdx] = stars[starIdx].size * eased;
      }
    }
    points.geometry.attributes.position.needsUpdate = true;
    points.geometry.attributes.size.needsUpdate = true;
  }
}
